<main class="p-4 p-xxl-5">
  <section class="card border rounded-3 mb-4">
    <div class="card-body p-4 d-flex align-items-center justify-content-between gap-4 flex-wrap">
      <div class="d-flex align-items-center gap-3">
        <span class="rounded-circle d-inline-flex align-items-center justify-content-center text-white flex-shrink-0 onboarding-icon-square-235 onboarding-bg-navy">
          <i class="bi bi-play"></i>
        </span>
        <div>
          <div class="fw-bold text-black mb-1">Activation is the handoff event</div>
          <p class="mb-0 text-muted">
            When an onboarding activates, OnboardOS commits its pack to Complyon and closes. The entity then lives in Complyon for its compliance lifetime.
          </p>
        </div>
      </div>

      <button type="button" class="btn btn-success d-inline-flex align-items-center gap-2 fw-semibold px-3">
        <i class="bi bi-play"></i>
        <span>Activate {{ ready_count }} ready</span>
      </button>
    </div>
  </section>

  <section class="card border rounded-3 overflow-hidden mb-4">
    <div class="card-header bg-white d-flex align-items-center justify-content-between flex-wrap gap-3 py-3 px-4">
      <h2 class="h5 fw-bold text-black mb-0">Activation queue</h2>
      <div class="d-flex align-items-center justify-content-end flex-wrap gap-2">
        <label class="onboarding-queue-search d-inline-flex align-items-center">
          <i class="bi bi-search" aria-hidden="true"></i>
          <input v-model="table_search" type="search" placeholder="Search table" @input="reset_table_page">
        </label>
        <span class="text-uppercase text-muted fw-semibold small">Sorted by readiness</span>
      </div>
    </div>

    <div class="table-responsive">
      <table class="table align-middle mb-0">
        <thead class="table-light">
          <tr>
            <th class="text-uppercase text-muted small fw-semibold py-3 ps-4">Onboarded Entity</th>
            <th class="text-uppercase text-muted small fw-semibold py-3">Journey</th>
            <th class="text-uppercase text-muted small fw-semibold py-3">Activation means</th>
            <th class="text-uppercase text-muted small fw-semibold py-3">Readiness</th>
            <th class="text-uppercase text-muted small fw-semibold py-3">Decision</th>
            <th class="text-uppercase text-muted small fw-semibold py-3 pe-4">Action</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="is_loading_queue">
            <td colspan="6" class="text-center text-muted py-5">Loading activation queue...</td>
          </tr>
          <tr v-else-if="queue_error">
            <td colspan="6" class="py-4">
              <div class="alert alert-warning mb-0">{{ queue_error }}</div>
            </td>
          </tr>
          <tr v-else-if="queue.length === 0">
            <td colspan="6" class="text-center text-muted py-5">No onboarding(s) found for activation.</td>
          </tr>
          <tr
            v-else
            v-for="item in paginated_queue"
            :key="item.id || item.case_name"
            class="cursor-pointer"
            tabindex="0"
            @click="open_activation_detail(item)"
            @keyup.enter="open_activation_detail(item)"
          >
            <td class="ps-4 fw-bold text-black py-3">{{ item.case_name }}</td>
            <td class="py-3">{{ item.journey }}</td>
            <td class="py-3">{{ item.activation_means }}</td>
            <td class="py-3">{{ item.readiness }}</td>
            <td class="py-3">
              <span :class="['badge rounded-pill fw-normal', decision_badge_class(item.decision_tone)]">
                {{ item.decision }}
              </span>
            </td>
            <td class="pe-4 py-3 onboarding-text-navy">
              <button type="button" :class="['btn btn-link p-0 text-decoration-none', action_class(item.action_tone)]" @click.stop="open_activation_detail(item)">
                {{ item.action }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <TablePagination
      :page="table_page"
      :page-size="table_page_size"
      :page-size-options="table_page_size_options"
      :total-rows="table_total_rows"
      :page-start="table_page_start"
      :page-end="table_page_end"
      :total-pages="table_total_pages"
      @update:page="set_table_page"
      @update:page-size="set_table_page_size"
    />
  </section>

  <div
    v-if="selected_activation"
    class="onboarding-case-detail-backdrop"
    @click="close_activation_detail"
  ></div>

  <aside
    v-if="selected_activation"
    :class="['onboarding-case-detail-drawer bg-white shadow-lg', { 'is-fullscreen': is_detail_fullscreen }]"
  >
    <header class="border-bottom p-4">
      <div class="d-flex align-items-start justify-content-between gap-3">
        <div class="min-w-0">
          <div class="text-uppercase text-muted fw-semibold small mb-3">
            {{ (selected_case && selected_case.journey) || 'Onboarding' }} onboarding &middot; Activation gate
          </div>
          <h2 class="h5 fw-bold text-black mb-2">{{ (selected_case && selected_case.name) || selected_activation.case_name }}</h2>
          <p class="mb-0 text-muted">{{ selected_case_subtitle() }}</p>
        </div>

        <div class="d-flex align-items-center gap-3 flex-shrink-0">
          <span :class="['badge rounded-pill fw-normal', decision_badge_class(selected_activation.decision_tone)]">
            <span class="me-1">&bull;</span>{{ selected_activation.decision }}
          </span>
          <button
            type="button"
            class="btn btn-link text-muted p-0"
            :aria-label="is_detail_fullscreen ? 'Exit fullscreen' : 'View fullscreen'"
            :title="is_detail_fullscreen ? 'Exit fullscreen' : 'View fullscreen'"
            @click="toggle_detail_fullscreen"
          >
            <i :class="['bi', is_detail_fullscreen ? 'bi-fullscreen-exit' : 'bi-fullscreen', 'fs-5']"></i>
          </button>
          <button type="button" class="btn btn-link text-muted p-0" aria-label="Close activation detail" @click="close_activation_detail">
            <i class="bi bi-x-lg fs-5"></i>
          </button>
        </div>
      </div>
    </header>

    <div class="onboarding-case-detail-body p-4">
      <nav class="bg-light rounded-3 p-1 d-flex align-items-center gap-2 mb-4" aria-label="Activation detail sections">
        <button
          v-for="tab in detail_tabs"
          :key="tab.id"
          type="button"
          :class="[
            'btn btn-sm flex-fill d-inline-flex align-items-center justify-content-center gap-2',
            active_detail_tab === tab.id ? 'bg-white border shadow-sm fw-semibold' : 'border-0 bg-transparent'
          ]"
          @click="set_detail_tab(tab.id)"
        >
          <i :class="['bi', tab.icon]"></i>
          <span>{{ tab.label }}</span>
          <span v-if="tab.count" class="badge rounded-pill text-bg-danger">{{ tab.count }}</span>
        </button>
      </nav>
      <section class="border rounded-3 bg-light-subtle px-4 py-3 mb-4" aria-label="Current activation action">
        <div class="text-uppercase text-muted fw-semibold small mb-2">Required Action</div>
        <p class="text-muted mb-0">{{ current_detail_action_copy() }}</p>
      </section>
      <section v-if="active_detail_tab === 'summary'">
        <div v-if="is_loading_detail" class="text-muted mb-4">Loading entity detail...</div>

        <dl class="row g-0 mb-4">
          <template v-for="row in summary_rows" :key="row.label">
            <dt class="col-4 text-muted fw-normal mb-3">{{ row.label }}</dt>
            <dd class="col-8 fw-semibold mb-3">{{ row.value }}</dd>
          </template>
          <dt class="col-4 text-muted fw-normal mb-3">Activation means</dt>
          <dd class="col-8 fw-semibold mb-3">{{ selected_activation.activation_means }}</dd>
          <dt class="col-4 text-muted fw-normal">Decision</dt>
          <dd class="col-8 fw-semibold">{{ selected_activation.decision }}</dd>
        </dl>

        <section class="mb-4">
          <div class="text-uppercase text-muted fw-semibold small mb-3">Overall readiness</div>
          <div class="d-flex align-items-center gap-3">
            <div class="progress flex-grow-1 onboarding-progress-track">
              <div
                class="progress-bar onboarding-progress-bar"
                :style="{ '--onboarding-progress-size': `${(selected_case && selected_case.progress) || 0}%`, '--onboarding-progress-color': progress_bar_color() }"
              ></div>
            </div>
            <span class="small fw-bold">{{ (selected_case && selected_case.progress) || 0 }}%</span>
          </div>
        </section>

        <section>
          <div class="text-uppercase text-muted fw-semibold small mb-3">Stage timeline</div>
          <div class="onboarding-case-timeline">
            <article
              v-for="item in stage_timeline"
              :key="item.title"
              :class="['onboarding-case-timeline-item', `is-${item.status}`]"
            >
              <span class="onboarding-case-timeline-icon">
                <i :class="['bi', item.icon]"></i>
              </span>
              <div>
                <div class="fw-semibold text-black">{{ item.title }}</div>
                <div class="text-muted small mt-1">{{ item.detail }}</div>
              </div>
            </article>
          </div>
        </section>
      </section>

      <section v-else-if="active_form_section">
        <div class="text-uppercase text-muted fw-semibold small mb-3">{{ active_form_section.label }}</div>
        <section
          v-for="sub_section in active_form_section.sub_sections"
          :key="sub_section.id"
          class="mb-4"
        >
          <div
            v-if="sub_section.label && sub_section.label !== active_form_section.label"
            class="text-uppercase text-muted fw-semibold small mb-3"
          >
            {{ sub_section.label }}
          </div>
          <div class="onboarding-answer-grid">
            <article
              v-for="question in sub_section.questions"
              :key="question.id"
              class="onboarding-answer-card"
            >
              <p class="onboarding-answer-card__label">
                {{ question.label }}
                <span v-if="question.is_mandatory" class="text-danger">*</span>
              </p>
              <div class="onboarding-answer-card__value">{{ question.answer }}</div>
            </article>
          </div>
        </section>
      </section>
    </div>

    <footer class="onboarding-case-detail-footer border-top bg-white p-3 d-flex align-items-center justify-content-between gap-3">
      <button type="button" class="btn btn-link text-muted d-inline-flex align-items-center gap-2 text-decoration-none">
        <i class="bi bi-stars"></i>
        <span>Ask AI</span>
      </button>
      <div class="d-flex align-items-center gap-2">
          <button
          type="button"
          class="btn btn-outline-danger d-inline-flex align-items-center gap-2"
          :disabled="workflow_action_in_progress === 'reject' || workflow_actions_loading"
          @click="run_selected_workflow_action('reject')"
        >
          <i class="bi bi-x-circle"></i>
          <span>{{ workflow_action_in_progress === 'reject' ? 'Rejecting...' : 'Reject' }}</span>
        </button>
        <button
          type="button"
          class="btn btn-outline-warning d-inline-flex align-items-center gap-2"
          :disabled="workflow_action_in_progress === 'request_info' || workflow_actions_loading"
          @click="run_selected_workflow_action('request_info')"
        >
          <i class="bi bi-send"></i>
          <span>{{ workflow_action_in_progress === 'request_info' ? 'Sending...' : 'Request info' }}</span>
        </button>
        <button
          v-if="can_advance_stage"
          type="button"
          class="btn btn-primary d-inline-flex align-items-center gap-2"
          :disabled="workflow_action_in_progress === 'advance' || workflow_actions_loading"
          @click="run_selected_workflow_action('advance')"
        >
          <i class="bi bi-arrow-right"></i>
          <span>{{ workflow_action_in_progress === 'advance' ? 'Advancing...' : 'Advance stage' }}</span>
        </button>
      </div>
    </footer>
    <div v-if="workflow_actions_loading || workflow_actions_error || workflow_action_feedback" class="px-3 pb-3">
      <div v-if="workflow_actions_loading" class="small text-muted">Loading workflow actions...</div>
      <div v-else-if="workflow_actions_error" class="alert alert-warning mb-0 py-2 px-3 small">{{ workflow_actions_error }}</div>
      <div
        v-else-if="workflow_action_feedback"
        :class="['alert mb-0 py-2 px-3 small', workflow_action_feedback_tone === 'success' ? 'alert-success' : workflow_action_feedback_tone === 'danger' ? 'alert-danger' : 'alert-warning']"
      >
        {{ workflow_action_feedback }}
      </div>
    </div>
  </aside>

  <section class="row g-4">
    <div class="col-12 col-xl-6">
      <article class="card border rounded-3 h-100 overflow-hidden">
        <div class="card-header bg-white p-4 d-flex align-items-center justify-content-between gap-3">
          <div>
            <h2 class="h5 fw-bold text-black mb-2">&rarr; Written to Complyon on activation</h2>
            <p class="mb-0 text-muted">OnboardOS commits these once; they become the entity's permanent compliance record.</p>
          </div>
          <span class="badge rounded-pill bg-secondary-subtle text-secondary-emphasis">
            <span class="me-1">&bull;</span>Outbound
          </span>
        </div>

        <div class="card-body p-0">
          <article
            v-for="item in outbound_items"
            :key="item.title"
            class="d-flex align-items-center gap-3 px-4 py-3 border-bottom border-light-subtle"
          >
            <span class="rounded-circle d-inline-flex align-items-center justify-content-center bg-info-subtle text-info-emphasis flex-shrink-0 onboarding-icon-square-17">
              <i :class="['bi', item.icon]"></i>
            </span>
            <div class="min-w-0 flex-grow-1">
              <div class="fw-bold text-black">{{ item.title }}</div>
              <div class="text-muted small">{{ item.detail }}</div>
            </div>
            <span class="text-uppercase text-muted fw-semibold small text-nowrap">&rarr; {{ item.target }}</span>
          </article>
        </div>
      </article>
    </div>

    <div class="col-12 col-xl-6">
      <article class="card border rounded-3 h-100 overflow-hidden">
        <div class="card-header bg-white p-4 d-flex align-items-center justify-content-between gap-3">
          <div>
            <h2 class="h5 fw-bold text-black mb-2">&larr; Inherited from Complyon</h2>
            <p class="mb-0 text-muted">OnboardOS executes these rules at intake; Complyon owns them.</p>
          </div>
          <span class="badge rounded-pill bg-secondary-subtle text-secondary-emphasis">
            <span class="me-1">&bull;</span>Inbound
          </span>
        </div>

        <div class="card-body p-0">
          <article
            v-for="item in inbound_items"
            :key="item.title"
            class="d-flex align-items-center gap-3 px-4 py-3 border-bottom border-light-subtle"
          >
            <span class="rounded-circle d-inline-flex align-items-center justify-content-center bg-secondary-subtle text-secondary-emphasis flex-shrink-0 onboarding-icon-square-17">
              <i :class="['bi', item.icon]"></i>
            </span>
            <div class="min-w-0 flex-grow-1">
              <div class="fw-bold text-black">{{ item.title }}</div>
              <div class="text-muted small">{{ item.detail }}</div>
            </div>
            <span class="text-uppercase text-muted fw-semibold small text-nowrap">&larr; {{ item.source }}</span>
          </article>
        </div>
      </article>
    </div>
  </section>
</main>
